/**
 * OmniClinicalBridge PHI Protection System
 * OmniAlera Technologies Holdings Inc.
 * 
 * HIPAA Compliance - Protected Health Information Safeguards
 * 
 * PHI includes: Names, addresses, dates, phone numbers, emails,
 * SSN, medical record numbers, health plan numbers, account numbers,
 * license numbers, device identifiers, URLs, IP addresses, biometrics,
 * photos, and any other unique identifying characteristic
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// PHI Categories
export type PHICategory = 
  | 'name'
  | 'address'
  | 'date'
  | 'phone'
  | 'email'
  | 'ssn'
  | 'mrn'        // Medical Record Number
  | 'healthPlan'
  | 'account'
  | 'license'
  | 'device'
  | 'url'
  | 'ip'
  | 'biometric'
  | 'photo'
  | 'other';

// Detection result
export interface PHIDetection {
  category: PHICategory;
  value: string;
  position: { start: number; end: number };
  confidence: 'high' | 'medium' | 'low';
  redacted: string;
}

// Consent status
export interface ConsentStatus {
  dataCollection: boolean;
  dataStorage: boolean;
  dataSharing: boolean;
  analytics: boolean;
  timestamp: Date;
  version: string;
}

// Session security
export interface SessionSecurity {
  lastActivity: Date;
  timeoutMinutes: number;
  isLocked: boolean;
  requiresReauth: boolean;
}

interface PHIContextType {
  // PHI Detection
  detectPHI: (text: string) => PHIDetection[];
  redactPHI: (text: string) => string;
  containsPHI: (text: string) => boolean;
  
  // Consent Management
  consent: ConsentStatus | null;
  updateConsent: (consent: Partial<ConsentStatus>) => void;
  hasValidConsent: () => boolean;
  
  // Session Security
  session: SessionSecurity;
  refreshActivity: () => void;
  lockSession: () => void;
  unlockSession: () => void;
  
  // Data Anonymization
  anonymizePatientData: <T extends Record<string, any>>(data: T) => T;
  generateAnonymousId: () => string;
  
  // Encryption helpers (for integration with backend)
  encryptForTransmission: (data: string) => Promise<string>;
  
  // Audit
  logPHIAccess: (action: string, context?: string) => void;
  
  // Settings
  settings: PHISettings;
  updateSettings: (settings: Partial<PHISettings>) => void;
}

interface PHISettings {
  autoDetectPHI: boolean;
  blockPHIInput: boolean;
  sessionTimeoutMinutes: number;
  requireConsentForFeatures: boolean;
  encryptLocalStorage: boolean;
}

const defaultSettings: PHISettings = {
  autoDetectPHI: true,
  blockPHIInput: true,
  sessionTimeoutMinutes: 15,
  requireConsentForFeatures: true,
  encryptLocalStorage: true
};

const PHIContext = createContext<PHIContextType | undefined>(undefined);

// PHI Detection Patterns
const PHI_PATTERNS: Record<PHICategory, RegExp[]> = {
  name: [
    /\b[A-Z][a-z]+\s+[A-Z][a-z]+\b/g,  // Basic name pattern
  ],
  address: [
    /\b\d+\s+[A-Za-z]+\s+(Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr)\b/gi,
    /\b[A-Za-z]+,\s*[A-Z]{2}\s+\d{5}(-\d{4})?\b/g,  // City, State ZIP
  ],
  date: [
    /\b(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(\d{2}|\d{4})\b/g,  // MM/DD/YYYY
    /\b\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\b/g,  // YYYY-MM-DD
  ],
  phone: [
    /\b(\+?1[-.]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
  ],
  email: [
    /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  ],
  ssn: [
    /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g,
  ],
  mrn: [
    /\bMRN[-:\s]?\d{6,10}\b/gi,
    /\bPatient\s*ID[-:\s]?\d{6,10}\b/gi,
  ],
  healthPlan: [
    /\b[A-Z]{3}\d{9}\b/g,  // Common health plan format
  ],
  account: [
    /\b\d{8,17}\b/g,  // Account numbers
  ],
  license: [
    /\b[A-Z]{1,2}\d{6,8}\b/g,  // License format
  ],
  device: [
    /\b[A-F0-9]{2}(:[A-F0-9]{2}){5}\b/gi,  // MAC address
  ],
  url: [
    /https?:\/\/[^\s]+/g,
  ],
  ip: [
    /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
  ],
  biometric: [],  // Handled separately
  photo: [],      // Handled separately
  other: []
};

// Redaction tokens
const REDACTION_TOKENS: Record<PHICategory, string> = {
  name: '[REDACTED NAME]',
  address: '[REDACTED ADDRESS]',
  date: '[REDACTED DATE]',
  phone: '[REDACTED PHONE]',
  email: '[REDACTED EMAIL]',
  ssn: '[REDACTED SSN]',
  mrn: '[REDACTED MRN]',
  healthPlan: '[REDACTED PLAN ID]',
  account: '[REDACTED ACCOUNT]',
  license: '[REDACTED LICENSE]',
  device: '[REDACTED DEVICE ID]',
  url: '[REDACTED URL]',
  ip: '[REDACTED IP]',
  biometric: '[REDACTED BIOMETRIC]',
  photo: '[REDACTED IMAGE]',
  other: '[REDACTED]'
};

export const PHIProtectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentStatus | null>(null);
  const [session, setSession] = useState<SessionSecurity>({
    lastActivity: new Date(),
    timeoutMinutes: 15,
    isLocked: false,
    requiresReauth: false
  });
  const [settings, setSettings] = useState<PHISettings>(defaultSettings);
  // PHI access log removed for build
  
  // Detect PHI in text
  const detectPHI = useCallback((text: string): PHIDetection[] => {
    const detections: PHIDetection[] = [];
    
    for (const [category, patterns] of Object.entries(PHI_PATTERNS)) {
      for (const pattern of patterns) {
        // Reset regex state
        pattern.lastIndex = 0;
        
        let match;
        while ((match = pattern.exec(text)) !== null) {
          detections.push({
            category: category as PHICategory,
            value: match[0],
            position: { start: match.index, end: match.index + match[0].length },
            confidence: 'medium',
            redacted: REDACTION_TOKENS[category as PHICategory]
          });
        }
      }
    }
    
    return detections;
  }, []);
  
  // Redact PHI from text
  const redactPHI = useCallback((text: string): string => {
    const detections = detectPHI(text);
    
    // Sort by position descending to avoid offset issues
    detections.sort((a, b) => b.position.start - a.position.start);
    
    let redactedText = text;
    for (const detection of detections) {
      redactedText = 
        redactedText.slice(0, detection.position.start) +
        detection.redacted +
        redactedText.slice(detection.position.end);
    }
    
    return redactedText;
  }, [detectPHI]);
  
  // Check if text contains PHI
  const containsPHI = useCallback((text: string): boolean => {
    return detectPHI(text).length > 0;
  }, [detectPHI]);
  
  // Update consent
  const updateConsent = useCallback((newConsent: Partial<ConsentStatus>) => {
    setConsent(prev => ({
      dataCollection: prev?.dataCollection ?? false,
      dataStorage: prev?.dataStorage ?? false,
      dataSharing: prev?.dataSharing ?? false,
      analytics: prev?.analytics ?? false,
      timestamp: new Date(),
      version: '1.0',
      ...newConsent
    }));
  }, []);
  
  // Check if consent is valid
  const hasValidConsent = useCallback((): boolean => {
    if (!consent) return false;
    
    // Check if consent is recent (within 1 year)
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    return consent.timestamp > oneYearAgo && consent.dataCollection;
  }, [consent]);
  
  // Refresh session activity
  const refreshActivity = useCallback(() => {
    setSession(prev => ({
      ...prev,
      lastActivity: new Date(),
      isLocked: false
    }));
  }, []);
  
  // Lock session
  const lockSession = useCallback(() => {
    setSession(prev => ({
      ...prev,
      isLocked: true,
      requiresReauth: true
    }));
  }, []);
  
  // Unlock session
  const unlockSession = useCallback(() => {
    setSession(prev => ({
      ...prev,
      isLocked: false,
      requiresReauth: false,
      lastActivity: new Date()
    }));
  }, []);
  
  // Anonymize patient data
  const anonymizePatientData = useCallback(<T extends Record<string, any>>(data: T): T => {
    const anonymized = { ...data };
    
    const sensitiveFields = ['name', 'firstName', 'lastName', 'email', 'phone', 'address', 'ssn', 'dob', 'dateOfBirth'];
    
    for (const field of sensitiveFields) {
      if (field in anonymized) {
        if (typeof anonymized[field] === 'string') {
          (anonymized as any)[field] = redactPHI((anonymized as any)[field]);
        } else {
          (anonymized as any)[field] = '[REDACTED]';
        }
      }
    }
    
    return anonymized;
  }, [redactPHI]);
  
  // Generate anonymous ID
  const generateAnonymousId = useCallback((): string => {
    return `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);
  
  // Encrypt for transmission (placeholder - would integrate with actual encryption)
  const encryptForTransmission = useCallback(async (data: string): Promise<string> => {
    // In production, this would use Web Crypto API or similar
    // For now, return base64 encoded (NOT secure - placeholder only)
    if (typeof btoa !== 'undefined') {
      return btoa(data);
    }
    return data;
  }, []);
  
  // Log PHI access
  const logPHIAccess = useCallback((action: string, context?: string) => {
    const entry = {
      timestamp: new Date(),
      action,
      context
    };
    
    // PHI access log disabled
    
    // In production, would also send to audit server
    console.log('[PHI_AUDIT]', entry);
  }, []);
  
  // Update settings
  const updateSettings = useCallback((newSettings: Partial<PHISettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);
  
  // Auto-lock session after timeout
  React.useEffect(() => {
    const checkTimeout = () => {
      const now = new Date();
      const timeSinceActivity = (now.getTime() - session.lastActivity.getTime()) / 1000 / 60;
      
      if (timeSinceActivity >= session.timeoutMinutes && !session.isLocked) {
        lockSession();
      }
    };
    
    const interval = setInterval(checkTimeout, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [session.lastActivity, session.timeoutMinutes, session.isLocked, lockSession]);
  
  const value: PHIContextType = {
    detectPHI,
    redactPHI,
    containsPHI,
    consent,
    updateConsent,
    hasValidConsent,
    session,
    refreshActivity,
    lockSession,
    unlockSession,
    anonymizePatientData,
    generateAnonymousId,
    encryptForTransmission,
    logPHIAccess,
    settings,
    updateSettings
  };
  
  return (
    <PHIContext.Provider value={value}>
      {children}
    </PHIContext.Provider>
  );
};

export const usePHIProtection = (): PHIContextType => {
  const context = useContext(PHIContext);
  if (!context) {
    throw new Error('usePHIProtection must be used within a PHIProtectionProvider');
  }
  return context;
};

// Utility hook for protected inputs
export function useProtectedInput(initialValue: string = '') {
  const [value, setValue] = useState(initialValue);
  const [warning, setWarning] = useState<string | null>(null);
  const { containsPHI, settings } = usePHIProtection();
  
  const handleChange = useCallback((newValue: string) => {
    if (settings.autoDetectPHI && containsPHI(newValue)) {
      setWarning('Warning: This input may contain protected health information (PHI)');
      
      if (settings.blockPHIInput) {
        return; // Don't update value
      }
    } else {
      setWarning(null);
    }
    
    setValue(newValue);
  }, [containsPHI, settings.autoDetectPHI, settings.blockPHIInput]);
  
  return { value, setValue: handleChange, warning };
}
